import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, ArrowLeft, ArrowRight, Copy, CheckCircle2 } from "lucide-react";

/**
 * /forgot-password — collect email, request reset link.
 *
 * The backend ALWAYS responds 200 to prevent email-enumeration. If the email
 * exists, the response includes a one-time reset token (until real email is
 * wired in). We surface that token visibly so the user can finish the flow,
 * with a one-tap copy + "Continue to reset" button.
 */
export default function ForgotPassword() {
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${backend}/api/auth/forgot-password`, { email });
            setResult(data || {});
            if (data?.reset_token) {
                toast.success("Reset link generated");
            } else {
                toast.success("If that email is on file, a reset link was generated.");
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't process the request. Please try again.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const resetUrl = result?.reset_token
        ? `${window.location.origin}/reset-password?token=${encodeURIComponent(result.reset_token)}`
        : null;

    const copyToken = async () => {
        if (!result?.reset_token) return;
        try {
            await navigator.clipboard.writeText(result.reset_token);
            toast.success("Token copied");
        } catch {
            toast.error("Copy failed — select the token manually");
        }
    };

    return (
        <div className="min-h-screen bg-gradient-hero">
            <div className="mx-auto max-w-md px-5 sm:px-8 py-10 sm:py-16">
                <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back-link">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
                <Card className="mt-6 p-8 sm:p-10 border-border shadow-elegant" data-testid="forgot-card">
                    <p className="text-xs font-mono uppercase tracking-[0.22em] text-muted-foreground">Account recovery</p>
                    <h1 className="mt-2 font-serif text-3xl sm:text-4xl leading-tight">Forgot your password?</h1>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">No problem — drop your email and we'll generate a one-time reset link valid for 60 minutes.</p>

                    {!result?.reset_token && (
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="email" className="text-sm">Account email</Label>
                                <div className="relative mt-2">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="pl-9 h-11 rounded-xl"
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                            </div>
                            <Button type="submit" disabled={loading || !email} className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="forgot-submit-btn">
                                {loading ? "Please wait…" : "Send me a reset link"}
                                <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                        </form>
                    )}

                    {result?.reset_token && (
                        <div className="mt-6 rounded-2xl border-2 border-success/40 bg-success/5 p-5" data-testid="forgot-token-block">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-success" />
                                <p className="font-medium text-foreground">Reset link generated</p>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                Email delivery isn't wired in yet — but your one-time token is below. Tap the button to continue and set a new password right now.
                            </p>
                            <div className="mt-4 flex items-center gap-2 rounded-full bg-card border border-border px-4 h-11">
                                <span className="text-xs font-mono text-muted-foreground truncate flex-1" data-testid="forgot-token-value">{result.reset_token}</span>
                                <button onClick={copyToken} className="text-muted-foreground hover:text-foreground" aria-label="Copy token" data-testid="forgot-copy-token">
                                    <Copy className="h-4 w-4" />
                                </button>
                            </div>
                            <Button asChild className="mt-4 w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="forgot-continue-reset">
                                <Link to={`/reset-password?token=${encodeURIComponent(result.reset_token)}`}>Continue → set new password</Link>
                            </Button>
                            <p className="mt-3 text-[11px] text-muted-foreground text-center">Link expires in {result.expires_in_minutes || 60} minutes.</p>
                        </div>
                    )}

                    {result && !result?.reset_token && (
                        <div className="mt-6 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground" data-testid="forgot-generic-message">
                            {result.message || "If that email is on file, a reset link was generated."}
                        </div>
                    )}
                </Card>

                <p className="mt-6 text-xs text-muted-foreground text-center">
                    Remembered it? <Link to="/auth" className="text-foreground underline-offset-4 hover:underline">Sign in</Link>.
                </p>
            </div>
        </div>
    );
}
